"use client";

import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/auth.store";
import { favoritesService } from "@/services/favorites.service";
import { useToastStore } from "@/store/toast.store";
import type { ListStoresParams, StoreListItem } from "@/types";

const DEFAULT_LIMIT = 20;

// ── List favorites (infinite) ──

interface UseFavoritesParams extends Omit<ListStoresParams, "page"> {
  limit?: number;
  enabled?: boolean;
}

export function useFavorites(params?: UseFavoritesParams) {
  const user = useAuthStore((s) => s.user);
  const limit = params?.limit ?? DEFAULT_LIMIT;

  return useInfiniteQuery<StoreListItem[]>({
    queryKey: ["favorites", { params }],
    queryFn: ({ pageParam }) => {
      return favoritesService.list({ ...params, page: pageParam as number, limit });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < limit) return undefined;
      return allPages.length + 1;
    },
    enabled: !!user && params?.enabled !== false,
    staleTime: 2 * 60 * 1000,
  });
}

// ── Toggle favorite (add / remove) ──

type InfiniteStoreData = { pages: StoreListItem[][]; pageParams: unknown[] };
type CarouselData = import("@/types").Carousel[];

function applyIsFavorite(
  queryClient: ReturnType<typeof useQueryClient>,
  storeUlid: string,
  value: boolean
) {
  // Update all paginated store lists
  queryClient.setQueriesData<InfiniteStoreData>(
    { queryKey: ["stores"], exact: false },
    (old) => {
      if (!old?.pages) return old;
      return {
        ...old,
        pages: old.pages.map((page) =>
          page.map((s) => (s.ulid === storeUlid ? { ...s, is_favorite: value } : s))
        ),
      };
    }
  );

  // Update store detail cache
  queryClient.setQueriesData<import("@/types").StoreDetail>(
    { queryKey: ["store-detail", storeUlid], exact: false },
    (old) => old ? { ...old, is_favorite: value } : old
  );

  // Update carousels that embed store items
  queryClient.setQueriesData<CarouselData>(
    { queryKey: ["carousels"], exact: false },
    (old) => {
      if (!Array.isArray(old)) return old;
      return old.map((carousel) => ({
        ...carousel,
        stores: carousel.stores.map((s) =>
          s.ulid === storeUlid ? { ...s, is_favorite: value } : s
        ),
      }));
    }
  );
}

export function useToggleFavorite() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.show);

  const add = useMutation({
    mutationFn: (storeUlid: string) =>
      favoritesService.add(storeUlid),
    onMutate: (storeUlid) => applyIsFavorite(queryClient, storeUlid, true),
    onError: (_err, storeUlid) => applyIsFavorite(queryClient, storeUlid, false),
    onSuccess: () => {
      showToast("Magasin sauvegardé", { label: "Voir les favoris", href: "/saved-stores" });
      queryClient.invalidateQueries({ queryKey: ["favorites"] });
      queryClient.invalidateQueries({ queryKey: ["stores"] });
      queryClient.invalidateQueries({ queryKey: ["carousels"] });
    },
  });

  const remove = useMutation({
    mutationFn: (storeUlid: string) =>
      favoritesService.remove(storeUlid),
    onMutate: (storeUlid) => applyIsFavorite(queryClient, storeUlid, false),
    onError: (_err, storeUlid) => applyIsFavorite(queryClient, storeUlid, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["favorites"] });
      queryClient.invalidateQueries({ queryKey: ["stores"] });
      queryClient.invalidateQueries({ queryKey: ["carousels"] });
    },
  });

  const toggle = (storeUlid: string, isFavorite: boolean) => {
    if (isFavorite) {
      remove.mutate(storeUlid);
    } else {
      add.mutate(storeUlid);
    }
  };

  const isPending = add.isPending || remove.isPending;

  return { toggle, isPending };
}
